import { useMemo, useCallback } from "react";
import { useNumbers } from "./use-numbers";

/**
 * Specialized hook for badge numbers with formatting and display logic
 * @param {Object} options - Configuration options
 * @param {number} options.maxDisplay - Maximum number to display before showing "99+" (default: 99)
 * @param {boolean} options.showZero - Whether to show badge when count is 0 (default: false)
 * @param {Object} options.filters - Custom filters for the numbers query
 * @returns {Object} Formatted badge numbers and utilities
 */
export const useBadgeNumbers = (options = {}) => {
  const { maxDisplay = 99, showZero = false, filters = {} } = options;

  const { numbers, isLoading, hasError, ...rest } = useNumbers({ filters });

  // Format numbers for display in badges
  const formatBadgeNumber = useCallback(
    (count) => {
      if (!showZero && count === 0) return null;
      if (count > maxDisplay) return `${maxDisplay}+`;
      return count;
    },
    [maxDisplay, showZero]
  );

  // Badge visibility helper
  const shouldShowBadge = useCallback(
    (count) => showZero || count > 0,
    [showZero]
  );

  // Memoize the formatted badge numbers
  const badgeNumbers = useMemo(
    () => ({
      packages: formatBadgeNumber(numbers.packages),
      pickups: formatBadgeNumber(numbers.pickups),
      invoices: formatBadgeNumber(numbers.invoices),
      tickets: formatBadgeNumber(numbers.tickets),
      returnVouchers: formatBadgeNumber(numbers.returnVouchers),
      users: formatBadgeNumber(numbers.users),
      unassignedPackages: formatBadgeNumber(numbers.unassignedPackages),
      unassignedPickups: formatBadgeNumber(numbers.unassignedPickups),
    }),
    [
      numbers.packages,
      numbers.pickups,
      numbers.invoices,
      numbers.tickets,
      numbers.returnVouchers,
      numbers.users,
      numbers.unassignedPackages,
      numbers.unassignedPickups,
      formatBadgeNumber,
    ]
  );

  const badgeVisibility = useMemo(
    () => ({
      packages: shouldShowBadge(numbers.packages),
      pickups: shouldShowBadge(numbers.pickups),
      invoices: shouldShowBadge(numbers.invoices),
      tickets: shouldShowBadge(numbers.tickets),
      returnVouchers: shouldShowBadge(numbers.returnVouchers),
      users: shouldShowBadge(numbers.users),
      unassignedPackages: shouldShowBadge(numbers.unassignedPackages),
      unassignedPickups: shouldShowBadge(numbers.unassignedPickups),
    }),
    [
      numbers.packages,
      numbers.pickups,
      numbers.invoices,
      numbers.tickets,
      numbers.returnVouchers,
      numbers.users,
      numbers.unassignedPackages,
      numbers.unassignedPickups,
      shouldShowBadge,
    ]
  );

  return {
    // Raw numbers (for reference)
    numbers,
    // Formatted numbers for badges
    badgeNumbers,
    // Visibility helpers
    badgeVisibility,
    // States
    isLoading,
    hasError,
    // Utility functions
    formatBadgeNumber,
    shouldShowBadge,
    // Pass through other properties from useNumbers
    ...rest,
  };
};

export default useBadgeNumbers;
